import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogHeader} from '@ui/overlays/dialog/dialog-header';
import {Trans} from '@ui/i18n/trans';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {useForm} from 'react-hook-form';
import {Form} from '@ui/forms/form';
import {useDialogContext} from '@ui/overlays/dialog/dialog-context';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {DialogFooter} from '@ui/overlays/dialog/dialog-footer';
import {Button} from '@ui/buttons/button';
import {
  ImportMediaItemPayload,
  useImportSingleFromTmdb,
} from '@app/admin/titles/requests/use-import-single-from-tmdb';
import {FormSelect, Option} from '@ui/forms/select/select';
import {Category, TITLE_MODEL} from '@app/titles/models/title';
import {PERSON_MODEL} from '@app/titles/models/person';
import React, {useEffect, useMemo, useState} from 'react';
import {Item} from '@ui/forms/listbox/item';
import {apiClient} from '@common/http/query-client';
import {useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';

interface ImportFromTmdbDialogProps {
  modelType: typeof TITLE_MODEL | typeof PERSON_MODEL;
}

interface Data {
  id: number;
  name: string;
}

function useCategories(mediaType: string, id: number | string) {
  const [categories, setCategories] = useState<Data[]>([]);
  const data = useBootstrapDataStore(s => s.data);
  const catsByModel = useMemo(() => {
    const entries = Object.entries(data?.cats_by_model || {});
    return entries.reduce(
      (acc, [key, value]) => {
        if (!Array.isArray(value)) {
          value = Object.values(value);
        }
        acc[key] = value;
        return acc;
      },
      {} as Record<string, Data[]>,
    );
  }, [data]);

  useEffect(() => {
    if (mediaType && Array.isArray(catsByModel[mediaType])) {
      let updatedCategories = [...catsByModel[mediaType]];

      if (mediaType === 'movie' || mediaType === 'series') {
        updatedCategories = [
          ...updatedCategories,
          ...(catsByModel['animes'] || []),
          ...(catsByModel['doc'] || []),
        ];
      }

      setCategories(updatedCategories);
    } else {
      setCategories([]);
    }
  }, [mediaType, catsByModel]);

  return categories;
}

export function ImportSingleFromTmdbDialog({
  modelType,
}: ImportFromTmdbDialogProps) {
  const form = useForm<ImportMediaItemPayload>({
    defaultValues: {
      media_type: modelType === TITLE_MODEL ? 'movie' : 'person',
    },
  });
  const {formId, close} = useDialogContext();
  const importItem = useImportSingleFromTmdb();
  const watch = form.watch;
  const media_type = watch('media_type');
  const id = watch('id');
  const categories = useCategories(media_type, id);

  return (
    <Dialog>
      <DialogHeader>
        <Trans message="Import title" />
      </DialogHeader>
      <DialogBody>
        <Form
          id={formId}
          form={form}
          onSubmit={values => {
            importItem.mutate(values, {
              onSuccess: response => {
                close(response.mediaItem);
              },
            });
          }}
        >
          {modelType === TITLE_MODEL && (
            <FormSelect
              name="media_type"
              label={<Trans message="Type" />}
              className="mb-24"
              selectionMode="single"
            >
              <Option value="movie">
                <Trans message="Movie" />
              </Option>
              <Option value="series">
                <Trans message="Series" />
              </Option>
              <Option value="games">
                <Trans message="Games" />
              </Option>
              <Option value="ebook">
                <Trans message="Ebooks" />
              </Option>
              <Option value="music">
                <Trans message="Music" />
              </Option>
            </FormSelect>
          )}
          {
            <div>
              <FormTextField
                autoFocus
                required
                className="mb-24"
                name="id"
                min={1}
                type="number"
                label={
                  <Trans
                    message={
                      media_type === 'games'
                        ? 'IGDB ID'
                        : media_type === 'ebook'
                          ? 'ISBN ID'
                          : media_type === 'music'
                            ? 'Deezer ID'
                            : 'TMDB ID'
                    }
                  />
                }
              />
              <FormSelect
                name="category"
                label={<Trans message="Category" />}
                className="mb-24"
                items={categories}
                selectionMode="single"
              >
                {item => (
                  <Item value={item.id}>
                    <Trans message={item.name} />
                  </Item>
                )}
              </FormSelect>
            </div>
          }
        </Form>
      </DialogBody>
      <DialogFooter>
        <Button onClick={() => close()}>
          <Trans message="Cancel" />
        </Button>
        <Button
          form={formId}
          variant="flat"
          color="primary"
          type="submit"
          disabled={importItem.isPending}
        >
          <Trans message="Import" />
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
